import {useCallback, forwardRef} from "react";
import {Box, BoxProps, Combobox, InputBase, useCombobox} from "@mantine/core";
import {TSubjectId, ISubject} from "@/common/types";
import {IconChevronDown} from "@tabler/icons-react";
import commonClasses from "../common.module.css";

interface SubjectSelectorProps extends BoxProps {
    /**
     * Subjects to allow selection from
     */
    subjects: Array<ISubject>
    /**
     * The id of the selected subject
     */
    selectedId?: TSubjectId
    /**
     * Whether this control should be disabled or not
     */
    disabled?: boolean
    onSelection: (subject: ISubject) => void
}

export const SubjectSelector = forwardRef<HTMLDivElement, SubjectSelectorProps>(({
                                                                                     onSelection,
                                                                                     subjects,
                                                                                     selectedId,
                                                                                     disabled = false,
                                                                                     ...others
                                                                                 }, ref) => {
        const combobox = useCombobox({
            onDropdownClose: () => combobox.resetSelectedOption(),
        });

        const setSelected = useCallback((id: TSubjectId) => {
            const subject = subjects.find((s) => s.id === id)
            if (subject)
                onSelection(subject)
            else {
                console.log(`Failed to find ${id} in ${JSON.stringify(subjects)}`)
            }
        }, [onSelection, subjects])

        const renderSubjectOptions = (s: Array<ISubject>) => {
            return s.map((s) =>
                (<Combobox.Option key={s.id} value={s.id}>{s.name}</Combobox.Option>)
            )
        }

        return (
            <Box ref={ref} {...others}>
                <Combobox store={combobox} disabled={disabled}
                          onOptionSubmit={(id) => {
                              setSelected(id)
                              combobox.closeDropdown()
                          }}
                >
                    <Combobox.Target>
                        <InputBase
                            classNames={{input: commonClasses.brightnessHover}}
                            label="Select Subject"
                            labelProps={{style: {textAlign: "center", width: "100%"}}}
                            component="button"
                            type="button"
                            pointer
                            rightSection={<IconChevronDown size="1rem"/>}
                            rightSectionPointerEvents="none"
                            onClick={() => combobox.toggleDropdown()}
                        >
                            {subjects.find((s) => s.id === selectedId)?.name}
                        </InputBase>
                    </Combobox.Target>
                    <Combobox.Dropdown mah={200} style={{overflowY: "auto"}}>
                        <Combobox.Options>
                            {renderSubjectOptions(subjects)}
                        </Combobox.Options>
                    </Combobox.Dropdown>
                </Combobox>
            </Box>
        )
    }
)